// 获取设备信息
const u = navigator.userAgent

export const device = {
  Android: function () {
    return navigator.userAgent.match(/Android/i) !== null
  },
  BlackBerry: function () {
    return navigator.userAgent.match(/BlackBerry/i) !== null
  },
  iOS: function () {
    return navigator.userAgent.match(/iPad|iPod|iPhone/i) !== null
  },
  iphone: function () {
    return navigator.userAgent.match(/iPhone/i) !== null
  },
  Windows: function () {
    return navigator.userAgent.match(/IEMobile/i) !== null
  },
  any: function () {
    return (this.Android() || this.BlackBerry() || this.Windows() || this.iOS())
  },
  wechat: function () {
    return navigator.userAgent.match(/MicroMessenger/i) !== null
  },
  qq: function () {
    return navigator.userAgent.match(/\sqq\//i) === ' qq/'
  },
  isIOS: function() {
    return !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  }
}

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  {number}    delay -          A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 * @param  {boolean}   [noTrailing] -   Optional, defaults to false. If noTrailing is true, callback will only execute every `delay` milliseconds while the
 *                                    throttled-function is being called. If noTrailing is false or unspecified, callback will be executed one final time
 *                                    after the last throttled-function call. (After the throttled-function has not been called for `delay` milliseconds,
 *                                    the internal counter is reset).
 * @param  {Function}  callback -       A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                    to `callback` when the throttled-function is executed.
 * @param  {boolean}   [debounceMode] - If `debounceMode` is true (at begin), schedule `clear` to execute after `delay` ms. If `debounceMode` is false (at end),
 *                                    schedule `callback` to execute after `delay` ms.
 *
 * @returns {Function}  A new, throttled, function.
 */
export function throttle(delay, noTrailing, callback, debounceMode) {
  /*
   * After wrapper has stopped being called, this timeout ensures that
   * `callback` is executed at the proper times in `throttle` and `end`
   * debounce modes.
   */
  let timeoutID
  let cancelled = false

  // Keep track of the last time `callback` was executed.
  let lastExec = 0

  // Function to clear existing timeout
  function clearExistingTimeout() {
    if (timeoutID) {
      clearTimeout(timeoutID)
    }
  }

  // Function to cancel next exec
  function cancel() {
    clearExistingTimeout()
    cancelled = true
  }

  // `noTrailing` defaults to falsy.
  if (typeof noTrailing !== 'boolean') {
    debounceMode = callback
    callback = noTrailing
    noTrailing = undefined
  }

  /*
   * The `wrapper` function encapsulates all of the throttling / debouncing
   * functionality and when executed will limit the rate at which `callback`
   * is executed.
   */
  function wrapper(...arguments_) {
    const self = this
    const elapsed = Date.now() - lastExec

    if (cancelled) {
      return
    }

    // Execute `callback` and update the `lastExec` timestamp.
    function exec() {
      lastExec = Date.now()
      callback.apply(self, arguments_)
    }

    /*
     * If `debounceMode` is true (at begin) this is used to clear the flag
     * to allow future `callback` executions.
     */
    function clear() {
      timeoutID = undefined
    }

    if (debounceMode && !timeoutID) {
      /*
       * Since `wrapper` is being called for the first time and
       * `debounceMode` is true (at begin), execute `callback`.
       */
      exec()
    }

    clearExistingTimeout()

    if (debounceMode === undefined && elapsed > delay) {
      /*
       * In throttle mode, if `delay` time has been exceeded, execute
       * `callback`.
       */
      exec()
    } else if (noTrailing !== true) {
      /*
       * In trailing throttle mode, since `delay` time has not been
       * exceeded, schedule `callback` to execute `delay` ms after most
       * recent execution.
       *
       * If `debounceMode` is true (at begin), schedule `clear` to execute
       * after `delay` ms.
       *
       * If `debounceMode` is false (at end), schedule `callback` to
       * execute after `delay` ms.
       */
      timeoutID = setTimeout(
        debounceMode ? clear : exec,
        debounceMode === undefined ? delay - elapsed : delay
      )
    }
  }

  wrapper.cancel = cancel

  // Return the wrapper function.
  return wrapper
}

/**
 * Debounce execution of a function. Debouncing, unlike throttling,
 * guarantees that a function is only executed a single time, either at the
 * very beginning of a series of calls, or at the very end.
 *
 * @param  {number}   delay -         A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 * @param  {boolean}  [atBegin] -     Optional, defaults to false. If atBegin is false or unspecified, callback will only be executed `delay` milliseconds
 *                                  after the last debounced-function call. If atBegin is true, callback will be executed only at the first debounced-function call.
 *                                  (After the throttled-function has not been called for `delay` milliseconds, the internal counter is reset).
 * @param  {Function} callback -      A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                  to `callback` when the debounced-function is executed.
 *
 * @returns {Function} A new, debounced function.
 */
export function debounce(delay, atBegin, callback) {
  return callback === undefined
    ? throttle(delay, atBegin, false)
    : throttle(delay, callback, atBegin !== false)
}

const WindowResources = []
/**
 * @description 动态引入资源，用于单易引入不用编译的js/css 减少编译负担
 * @param {string} src 资源路径
 * @param {boolean} noLoad 跳过加载直接执行回调
 */
export const loadResources = (src, noLoad) => {
  return new Promise((resolve, reject) => {
    if (WindowResources.includes(src) || noLoad) {
      resolve()
      return
    }
    if (!src) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject()
      return (console.log(new Error('please set the src of script/css')))
    }
    // load javascript
    if (/\.js(\?v=.*){0,1}$/.test(src)) {
      const script = document.createElement('script')
      script.src = src
      script.type = 'text/javascript'
      const headEle = document.getElementsByTagName('head')[0]
      // IE
      const IeScript = script
      if (IeScript.readyState) {
        IeScript.onreadystatechange = () => {
          if (IeScript.readyState === 'loaded' || IeScript.readyState === 'complete') {
            resolve()
            IeScript.onreadystatechange = null
          }
        }
        // webkit opera
      } else {
        script.onload = () => {
          resolve()
        }
      }
      headEle.appendChild(script)
      WindowResources.push(src)
    // load stylesheet
    } else if (/\.css$/.test(src)) {
      const styleSheet = document.createElement('link')
      styleSheet.href = src
      styleSheet.rel = 'stylesheet'
      const headEle = document.getElementsByTagName('head')[0]
      headEle.appendChild(styleSheet)
      WindowResources.push(src)
    }
  })
}
